import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from '@phosphor-icons/react';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

/**
 * Shared bottom sheet primitive.
 *
 * Wrap any modal content that slides up from the bottom in this component.
 * Replaces ~30 lines of boilerplate (AnimatePresence, motion config,
 * backdrop, body scroll lock, close-on-backdrop) per sheet.
 *
 * Props:
 * - isOpen:           boolean — controls mount + animation
 * - onClose:          () => void — called on backdrop tap, X tap, ESC (when keyboard support is added)
 * - children:         sheet contents (render whatever shape you want)
 * - closeOnBackdrop:  default true. Set false for forced-interaction sheets (Welcome, Onboarding)
 * - showCloseButton:  default false. When true, renders a floating X above the sheet
 * - maxHeight:        CSS height string, default '90dvh'
 * - className:        extra classes on the sheet container (default white bg + rounded top corners)
 * - backdropClassName: extra classes on the backdrop (default bg-black/50)
 * - zIndex:           default 50. Welcome / critical overlays may need higher.
 *
 * Children should handle their own scroll. The sheet container is flex column;
 * if your content can grow, wrap it in <div className="flex-1 overflow-y-auto">.
 */
export default function BottomSheet({
  isOpen,
  onClose,
  children,
  closeOnBackdrop = true,
  showCloseButton = false,
  maxHeight = '90dvh',
  className = '',
  backdropClassName = '',
  zIndex = 50,
}) {
  useBodyScrollLock(isOpen);

  const handleBackdropClick = () => {
    if (closeOnBackdrop) onClose?.();
  };

  // Portal a document.body para escapar cualquier transform ancestor (como el
  // slider de MarketLayout). Sin esto, `fixed inset-0` se posiciona relativo
  // al transformed ancestor en vez del viewport → el sheet no cubre toda la
  // pantalla. SSR-safe: si document no existe, no renderiza nada.
  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 flex flex-col items-center justify-end pointer-events-auto"
          style={{ zIndex }}
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={handleBackdropClick}
            onTouchMove={(e) => e.preventDefault()}
            className={`absolute inset-0 bg-black/50 ${backdropClassName}`}
          />

          {/* Floating X button — fades in shortly after sheet settles, fades out instantly on close */}
          {showCloseButton && (
            <motion.button
              type="button"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { delay: 0.3, duration: 0.2 } }}
              exit={{ opacity: 0, transition: { duration: 0.15 } }}
              onClick={onClose}
              className="relative mb-3 w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center z-10 active:scale-95 transition-transform"
              aria-label="Cerrar"
            >
              <X size={22} weight="bold" className="text-white" />
            </motion.button>
          )}

          {/* Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            className={`relative w-full max-w-md bg-white rounded-t-3xl flex flex-col overflow-hidden shadow-xl ${className}`}
            style={{ maxHeight }}
          >
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
